"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User } from 'firebase/auth';
import FirebaseService from '@/lib/firebase';
import type { Assistant } from '@/lib/firebase';

interface AssistantWithMessage extends Assistant {
  lastMessage?: string;
  lastMessageTime?: Date;
}

interface AssistantCacheContextType {
  assistants: AssistantWithMessage[];
  loading: boolean;
  refreshAssistants: () => Promise<void>;
}

const AssistantCacheContext = createContext<AssistantCacheContextType | undefined>(undefined);

export function AssistantCacheProvider({
  children,
  currentUser,
  fetchMessages = false, // New optional prop to control message fetching
  limit, // Optional limit for number of assistants to return
}: {
  children: ReactNode;
  currentUser: User | null;
  fetchMessages?: boolean;
  limit?: number;
}) {
  const [assistants, setAssistants] = useState<AssistantWithMessage[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAssistants = useCallback(async () => {
    if (!currentUser) {
      // When logged out, still fetch public assistants
      try {
        setLoading(true);
        const publicAssistants = await FirebaseService.getPublicAssistants();
        setAssistants(publicAssistants.map(a => ({ ...a, lastMessage: '', lastMessageTime: new Date(a.createdAt || Date.now()) })));
      } catch (error) {
        console.error('Error fetching public assistants:', error);
        setAssistants([]);
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      const startTime = performance.now();
      console.log('[AssistantCache] Starting fetch...');

      // Get all assistants the user has access to
      const [userAssistants, publicAssistants, sharedByEmail, sharedByDomain] = await Promise.all([
        FirebaseService.getUserAssistants(currentUser.uid),
        FirebaseService.getPublicAssistants(),
        currentUser.email ? FirebaseService.getSharedAssistantsByEmail(currentUser.email) : Promise.resolve([]),
        currentUser.email?.split('@')[1] ? FirebaseService.getSharedAssistantsByDomain(currentUser.email.split('@')[1]) : Promise.resolve([])
      ]);

      console.log(`[AssistantCache] Parallel fetches took ${(performance.now() - startTime).toFixed(0)}ms`);

      // Combine and deduplicate by assistantId
      const allAssistants = [
        ...userAssistants,
        ...publicAssistants,
        ...sharedByEmail,
        ...sharedByDomain,
      ];

      // Sort ALL assistants by lastAccessedAt FIRST (before fetching messages)
      const uniqueAssistants = Array.from(
        new Map(allAssistants.map(a => [a.assistantId, a])).values()
      );

      uniqueAssistants.sort((a, b) => {
        const aTime = Number(a.lastAccessedAt || a.createdAt || 0);
        const bTime = Number(b.lastAccessedAt || b.createdAt || 0);
        return bTime - aTime;
      });

      // Apply limit BEFORE fetching messages (critical for performance!)
      const limitedAssistants = limit ? uniqueAssistants.slice(0, limit) : uniqueAssistants;
      console.log(`[AssistantCache] Processing ${limitedAssistants.length} assistants (limit: ${limit || 'none'})`);

      // Only fetch messages if requested (expensive operation)
      if (fetchMessages) {
        console.log('[AssistantCache] Fetching messages for assistants...');
        const messageStart = performance.now();
        const assistantsWithMessages = await Promise.all(
          limitedAssistants.map(async (assistant) => {
            let lastMessage = '';
            let lastMessageTime = new Date(assistant.createdAt || Date.now());

            try {
              const messages = await FirebaseService.getMessages(assistant.assistantId, 1);
              if (messages && messages.length > 0) {
                const lastMsg = messages[messages.length - 1];
                lastMessage = lastMsg.content || '';
                lastMessageTime = lastMsg.timestamp
                  ? new Date(lastMsg.timestamp)
                  : lastMessageTime;
              }
            } catch (error) {
              console.error(`Error fetching messages for ${assistant.assistantId}:`, error);
            }

            return {
              ...assistant,
              lastMessage,
              lastMessageTime,
            };
          })
        );
        console.log(`[AssistantCache] Message fetching took ${(performance.now() - messageStart).toFixed(0)}ms`);

        setAssistants(assistantsWithMessages);
      } else {
        // Skip message fetching - much faster!
        const assistantsWithoutMessages = limitedAssistants.map(a => ({
          ...a,
          lastMessage: '',
          lastMessageTime: new Date(a.createdAt || Date.now())
        }));

        setAssistants(assistantsWithoutMessages);
      }

      console.log(`[AssistantCache] Total time: ${(performance.now() - startTime).toFixed(0)}ms`);
    } catch (error) {
      console.error('Error fetching assistants:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser, fetchMessages, limit]);

  useEffect(() => {
    fetchAssistants();
  }, [fetchAssistants]);

  const refreshAssistants = useCallback(async () => {
    await fetchAssistants();
  }, [fetchAssistants]);

  return (
    <AssistantCacheContext.Provider value={{ assistants, loading, refreshAssistants }}>
      {children}
    </AssistantCacheContext.Provider>
  );
}

export function useAssistantCache() {
  const context = useContext(AssistantCacheContext);
  if (context === undefined) {
    throw new Error('useAssistantCache must be used within an AssistantCacheProvider');
  }
  return context;
}
